import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, Link, useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';

const Bar = styled.header`
  position: sticky;
  top: 0;
  z-index: 50;
  backdrop-filter: blur(18px) saturate(160%);
  -webkit-backdrop-filter: blur(18px) saturate(160%);
  background: ${({ $scrolled }) =>
    $scrolled ? 'rgba(7, 6, 15, 0.78)' : 'rgba(7, 6, 15, 0.3)'};
  border-bottom: 1px solid ${({ $scrolled }) =>
    $scrolled ? 'var(--border)' : 'transparent'};
  transition: background 0.3s ease, border-color 0.3s ease;
`;

const Inner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 18px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
`;

const Brand = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 700;
  font-size: 1.1rem;
  letter-spacing: 0.2px;
`;

const Mark = styled.img`
  width: 38px;
  height: 38px;
  object-fit: contain;
  filter: drop-shadow(0 6px 16px rgba(61, 214, 255, 0.35));
`;

const DesktopNav = styled.nav`
  display: flex;
  align-items: center;
  gap: 4px;
  @media (max-width: 720px) {
    display: none;
  }
`;

const Item = styled(NavLink)`
  position: relative;
  padding: 10px 16px;
  border-radius: 999px;
  font-size: 0.95rem;
  color: var(--text-dim);
  font-weight: 500;
  transition: color 0.2s ease, background 0.2s ease;
  &:hover {
    color: var(--text);
    background: var(--surface);
  }
  &.active {
    color: var(--text);
    background: var(--surface-strong);
  }
`;

const Burger = styled.button`
  display: none;
  background: var(--surface);
  border: 1px solid var(--border);
  width: 40px;
  height: 40px;
  border-radius: 12px;
  color: var(--text);
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 110;
  @media (max-width: 720px) {
    display: inline-flex;
  }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100;
  background: #07060f;
  display: flex;
  flex-direction: column;
  ${({ $open }) =>
    $open
      ? css`
          opacity: 1;
          pointer-events: auto;
        `
      : css`
          opacity: 0;
          pointer-events: none;
        `}
  transition: opacity 0.25s ease;
`;

const OverlayBg = styled.div`
  position: absolute;
  inset: 0;
  background:
    radial-gradient(600px 400px at 80% 0%, rgba(255, 61, 127, 0.18), transparent 60%),
    radial-gradient(600px 400px at 0% 100%, rgba(124, 92, 255, 0.18), transparent 60%);
  pointer-events: none;
`;

const OverlayHeader = styled.div`
  position: relative;
  z-index: 1;
  padding: 18px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid var(--border);
`;

const OverlayList = styled.nav`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  padding: 28px 20px;
  gap: 6px;
`;

const OverlayItem = styled(NavLink)`
  display: block;
  padding: 18px 20px;
  border-radius: 16px;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.4rem;
  font-weight: 600;
  color: var(--text-dim);
  background: var(--surface);
  border: 1px solid var(--border);
  transition: color 0.2s ease, background 0.2s ease, border-color 0.2s ease;
  &:hover, &.active {
    color: var(--text);
    background: var(--surface-strong);
    border-color: var(--border-strong);
  }
`;

const OverlayFooter = styled.div`
  margin-top: auto;
  padding: 24px;
  position: relative;
  z-index: 1;
  border-top: 1px solid var(--border);
  color: var(--text-mute);
  font-size: 0.85rem;
  display: flex;
  flex-direction: column;
  gap: 4px;
  a { color: var(--text-dim); }
`;

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close menu on route change
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  const close = () => setOpen(false);

  const overlay = (
    <Overlay $open={open} aria-hidden={!open}>
      <OverlayBg />
      <OverlayHeader>
        <Brand to="/" onClick={close}>
          <Mark src="/zi-logo.png" alt="Zero Immersive" />
          Zero Immersive
        </Brand>
        <Burger
          aria-label="Close menu"
          onClick={close}
          style={{ display: 'inline-flex' }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="6" y1="6" x2="18" y2="18" />
            <line x1="6" y1="18" x2="18" y2="6" />
          </svg>
        </Burger>
      </OverlayHeader>
      <OverlayList>
        <OverlayItem to="/" end onClick={close}>Home</OverlayItem>
        <OverlayItem to="/about" onClick={close}>About & Contact</OverlayItem>
        <OverlayItem as="a" href="/terms.html" onClick={close}>Terms</OverlayItem>
        <OverlayItem as="a" href="/privacy.html" onClick={close}>Privacy</OverlayItem>
      </OverlayList>
      <OverlayFooter>
        <span>Zero Immersive Inc · Mississauga, Canada</span>
        <a href="mailto:accounts@zeroimmersive.com">accounts@zeroimmersive.com</a>
      </OverlayFooter>
    </Overlay>
  );

  return (
    <>
      <Bar $scrolled={scrolled}>
        <Inner>
          <Brand to="/" onClick={close}>
            <Mark src="/zi-logo.png" alt="Zero Immersive" />
            Zero Immersive
          </Brand>

          <DesktopNav>
            <Item to="/" end>Home</Item>
            <Item to="/about">About & Contact</Item>
            <Item as="a" href="/terms.html">Terms</Item>
            <Item as="a" href="/privacy.html">Privacy</Item>
          </DesktopNav>

          <Burger
            aria-label="Open menu"
            aria-expanded={open}
            onClick={() => setOpen(true)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="4" y1="7" x2="20" y2="7" />
              <line x1="4" y1="12" x2="20" y2="12" />
              <line x1="4" y1="17" x2="20" y2="17" />
            </svg>
          </Burger>
        </Inner>
      </Bar>
      {typeof document !== 'undefined' && createPortal(overlay, document.body)}
    </>
  );
}
